import React, { ChangeEvent, useCallback } from "react"

import {
  normalizeDecimalNumberInput,
  trimBeforeDecimals,
  trimDecimals,
} from "#/shared/utils"

import AmountInput, { AmountInputProps } from "./AmountInput"

export type TokenAmountInputProps = Required<
  Pick<AmountInputProps, "token" | "balanceToken">
> &
  Omit<AmountInputProps, "onChange" | "onBlur"> & {
    tokenDecimals: number
    onChange: (value: string) => void
    onBlur?: (value: string) => void
  }

export default function TokenAmountInput({
  balanceToken,
  token,
  tokenDecimals,
  onChange,
  onBlur,
  ...props
}: TokenAmountInputProps) {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const normalizedValue = normalizeDecimalNumberInput(event.target.value)
      const trimmedDecimals = trimDecimals(normalizedValue, tokenDecimals)
      const trimmedValue = trimBeforeDecimals(trimmedDecimals, tokenDecimals)

      onChange(trimmedValue)
    },
    [onChange, tokenDecimals],
  )

  const handleBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let { value } = event.target

      if (!!value && !Number.isNaN(value)) {
        const trimmedZeros = parseFloat(value).toString()
        value = trimmedZeros
      }

      onChange(value)
      onBlur?.(value)
    },
    [onBlur, onChange],
  )

  return (
    <AmountInput
      token={token}
      balanceToken={balanceToken}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    />
  )
}
